import React, { useState } from "react";
import {
  CreditCardOutlined,
  FileDoneOutlined,
  DollarCircleOutlined,
  LoadingOutlined,
  CloudUploadOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import { Button, Descriptions, Steps, Form, Input, Card, Alert, Spin, message } from "antd";
import { Typography } from "antd";
import { useNavigate } from "react-router-dom";
import waiting from "../../images/FP_Waiting.svg";
import check from "../../images/checkmark1.svg";
import { useDispatch, useSelector } from "react-redux";
import { createPaymentRequest, sendSubmitRequest } from "../../actions/Actions";
import Title from "antd/es/typography/Title";
import apiClient from "../../api/apiClient";
import { formatAmount } from "../../util";
import { useAuth0 } from "@auth0/auth0-react";

const { Text } = Typography;

const Confirmation = () => {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const transaction = useSelector((state) => state.transaction);
  const loanData = useSelector((state) => state.loanData);
  const [form] = Form.useForm();
  
  // State management for payment request
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [paymentRequestSubmitted, setPaymentRequestSubmitted] = useState(false);
  
  let steps = [
    {
      title: <span style={{color:"#33CB2B", fontWeight:"bold"}}>Loan Details</span>,
      status: "finish",
      icon: <CreditCardOutlined style={{color:"#33CB2B"}}/>,
    },
    {
      title: <span style={{color:"#33CB2B", fontWeight:"bold"}}>Funding Details</span>,
      status: "finish",
      icon: <DollarCircleOutlined style={{color:"#33CB2B"}}/>,
    },
    {
      title: <span style={{color:"#33CB2B", fontWeight:"bold"}}>Upload Documents</span>,
      status: "finish",
      icon: <CloudUploadOutlined style={{color:"#33CB2B"}}/>,
    },
    {
      title: <span style={{color:"#33CB2B", fontWeight:"bold"}}>Transaction Summary</span>,
      status: "finish",
      icon: <SolutionOutlined style={{color:"#33CB2B"}} />,
    },
    {
      title: <span style={{color:(transaction.transaction.status === "Completed" ?"#33CB2B":"#1677ff"), fontWeight:"bold"}}>Buyers Approval</span>,
      status: "process",
      icon: <FileDoneOutlined style={{color:(transaction.transaction.status === "Completed" ?"#33CB2B":"#1677ff")}} />,
    },
  ];

  const handleClick = () => {
    navigate("/");
  };
  
  const handleResend = () => {
    dispatch(sendSubmitRequest({"id":transaction.currentTransactionId}))
  };

  const handlePaymentRequest = async (values) => {
    console.log('Payment request data:', values);
    
    // Reset states
    setLoading(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // Prepare payment data
      const paymentData = {
        transaction_id: transaction.transaction.id,
        name: values.name,
        email: values.email,
        cell_phone: parseInt(values.cellPhone.replace(/\D/g, '')), // Remove non-numeric characters
        title: values.position,
        user: user?.email || user?.name || 'admin_user' // Use auth0 user info
      };
      
      console.log('Sending payment data:', paymentData);

      // Call API
      const response = await createPaymentRequest(paymentData);
      
      if (response.success) {
        setSuccessMessage('Payment request created successfully!');
        setPaymentRequestSubmitted(true);
        message.success('Payment request submitted successfully!');
        console.log('Payment request created:', response.data);
        
        // Reset form after successful submission
        form.resetFields();
        
        // Optional: Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
        
      } else {
        setError(response.message || 'Failed to create payment request');
        message.error(response.message || 'Failed to create payment request');
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error ||
                          err.message || 
                          'An error occurred while creating the payment request';
      setError(errorMessage);
      message.error(errorMessage);
      console.error('Error creating payment request:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Steps items={steps} />

      {transaction.transaction &&
      transaction.transaction.status === "Completed" ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#F3F3F3",
              padding: 15,
            }}
          >
            <img src={check} height="100px" alt="logo" />
            <Text>Buyer Approved Your Transaction of {formatAmount(transaction?.transaction?.amount)}</Text>
            <Text>Your Loan Funding is In Progress by {loanData?.lenderNm}</Text>
          </div>
          
          <br />
          
          {/* Show success message */}
          {successMessage && (
            <Alert
              message="Success"
              description={successMessage}
              type="success"
              showIcon
              closable
              onClose={() => setSuccessMessage('')}
              style={{ marginBottom: 16 }}
            />
          )}
          
          {/* Show error message */}
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Card 
            title="Request Payment Now" 
            style={{ marginBottom: 16 }}
            extra={paymentRequestSubmitted && <Text type="success">✓ Request Submitted</Text>}
          >
            <Spin spinning={loading} tip="Submitting payment request...">
              <Form
                form={form}
                layout="vertical"
                onFinish={handlePaymentRequest}
                initialValues={{
                  position: 'Finance Manager'
                }}
                disabled={loading || paymentRequestSubmitted}
              >
                <Form.Item
                  label="Name of person requesting payment"
                  name="name"
                  rules={[
                    { required: true, message: 'Please input the name!' },
                    { min: 2, message: 'Name must be at least 2 characters!' }
                  ]}
                >
                  <Input 
                    placeholder="Enter full name" 
                    maxLength={255}
                  />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please input the email!' },
                    { type: 'email', message: 'Please enter a valid email!' }
                  ]}
                >
                  <Input 
                    placeholder="Enter email address" 
                    maxLength={255}
                  />
                </Form.Item>

                <Form.Item
                  label="Cell Phone"
                  name="cellPhone"
                  rules={[
                    { required: true, message: 'Please input the cell phone number!' },
                    { 
                      pattern: /^[0-9]{10}$/, 
                      message: 'Please enter a valid 10-digit phone number!' 
                    }
                  ]}
                >
                  <Input 
                    placeholder="Enter 10-digit cell phone number (e.g., 2141234567)" 
                    maxLength={10}
                  />
                </Form.Item>

                <Form.Item
                  label="Position"
                  name="position"
                >
                  <Input disabled />
                </Form.Item>

                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={loading}
                    disabled={paymentRequestSubmitted}
                  >
                    {loading ? 'Submitting...' : paymentRequestSubmitted ? 'Request Submitted' : 'Submit Payment Request'}
                  </Button>
                  
                  {paymentRequestSubmitted && (
                    <Button 
                      type="link" 
                      onClick={() => {
                        setPaymentRequestSubmitted(false);
                        form.resetFields();
                        setSuccessMessage('');
                        setError(null);
                      }}
                      style={{ marginLeft: 8 }}
                    >
                      Submit Another Request
                    </Button>
                  )}
                </Form.Item>
              </Form>
            </Spin>
          </Card>
        </>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#F3F3F3",
            padding: 15,
          }}
        >
          <img src={waiting} height="100px" alt="logo" />
          <br />
          <Title level={4}>Please ask the buyer to approve the deal in their phone. An email has been sent to the buyer.</Title>
          <br />
        </div>
      )}

      <br />
      <Button type="primary" onClick={handleClick} style={{ float: "right" }}>
        Close
      </Button>
      {transaction.transaction.status !== "Completed" && (
        <>
          <Button 
            type="primary" 
            onClick={handleResend} 
            style={{ float: "right", marginRight:"5px" }}
          >
            Resend Request
          </Button>
          <Button 
            onClick={() => navigate("/summary")} 
            style={{ float: "right", marginRight:"5px" }}
          >
            Back
          </Button>
        </>
      )}
      {process.env.REACT_APP_ENV === 'DEV' && (
        <a 
          href="#" 
          onClick={() => apiClient.get(process.env.REACT_APP_BASE_URL + `/api/submitTransaction?id=${transaction.currentTransactionId}`)} 
        >
          *
        </a>
      )}
    </>
  );
};

export default Confirmation;
